import styled from "@emotion/styled";
import { colors as themeColors } from 'src/theme';
import { Flex, InputLabel } from 'src/components';
import { ValidationErrorStyle } from "src/common";

const Container = styled.div((props: { colors: { background?: string, text?: string, backgroundSelected?: string, textSelected?: string } }) => ({
  borderRadius: '12px',
  padding: '4px',
  backgroundColor: props.colors.background,
}));

const OptionContainer = styled(Flex)({
  gap: '4px',
});

const Option = styled.div((props: { isSelected: boolean, colors: { background?: string, text?: string, backgroundSelected?: string, textSelected?: string } }) => ({
  flex: 1,
  color: props.isSelected ? props.colors.textSelected : props.colors.backgroundSelected,
  backgroundColor: props.isSelected ? props.colors.backgroundSelected : props.colors.background,
  cursor: 'pointer',
  textAlign: 'center' as const,
  padding: '8px 8px',
  borderRadius: '8px',
  fontSize: '14px',
  fontWeight: '400',
  lineHeight: '20px',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    opacity: 0.9,
  },
}));

const InputLabelStyle = styled(InputLabel)({
  marginBottom: '8px',
});

interface SelectOption {
  value: string;
  label: string;
}

interface SwitchMultiSelectProps {
  value?: string[];
  options: SelectOption[];
  onChange?: (value: string[]) => void;
  className?: string;
  label?: string;
  error?: string;
  disabled?: boolean;
  colors?: {
    background?: string;
    text?: string;
    backgroundSelected?: string;
    textSelected?: string;
  };
}

export const SwitchMultiSelect: React.FC<SwitchMultiSelectProps> = ({
  value = [],
  options,
  onChange,
  className,
  label,
  error,
  disabled = false,
  colors: customColors = {},
}) => {
  const colors = {
    background: themeColors.primary4,
    text: themeColors.text2,
    backgroundSelected: themeColors.primary1,
    textSelected: themeColors.text7,
    ...customColors
  };

  const handleToggle = (optionValue: string) => {
    if (disabled) return;
    const isSelected = value.includes(optionValue);
    const newValue = isSelected
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    if (onChange) onChange(newValue);
  };

  return (
    <>
      {label ? <InputLabelStyle>{label}</InputLabelStyle> : null}
      <Container className={className} colors={colors}>
        <OptionContainer>
          {options.map((option) => (
            <Option
              key={option.value}
              onClick={() => handleToggle(option.value)}
              isSelected={value.includes(option.value)}
              colors={colors}
            >
              {option.label}
            </Option>
          ))}
        </OptionContainer>
      </Container>
      {error ? <ValidationErrorStyle>{error}</ValidationErrorStyle> : null}
    </>
  );
};
